import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AdvicePanel from "@/components/AdvicePanel";
import AppShell, { ErrorNote } from "@/components/AppShell";
import ComparatorBar from "@/components/ComparatorBar";
import ReportButton from "@/components/ReportButton";
import { api } from "@/lib/api";
import { formatDateTime, useAsync, useVesselPath } from "@/lib/hooks";
import {
  CLARITY_LABELS,
  EXTRA_PARAMS,
  FULL_PARAMS,
  PARAMS,
  type ParamKey,
  formatValue,
  readValue,
} from "@/lib/params";
import type { Reading } from "@/types";

const ORDER: ParamKey[] = [...FULL_PARAMS, ...EXTRA_PARAMS];

export default function ReadingDetail() {
  const vp = useVesselPath();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, error, loading } = useAsync<Reading>(
    () => api.get<Reading>(`/api/readings/${id}`),
    [id],
  );

  async function remove() {
    setDeleting(true);
    setActionError(null);
    try {
      await api.delete(`/api/readings/${id}`);
      navigate(vp("/history"), { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not delete the reading.");
      setDeleting(false);
    }
  }

  return (
    <AppShell
      title="Reading"
      subtitle={data ? formatDateTime(data.taken_at) : undefined}
      action={
        <div className="flex shrink-0 items-center gap-2">
          <Link to={vp("/history")} className="btn btn-quiet px-3 py-2 text-sm">
            Back
          </Link>
          {/* Only offered once there is something to edit. */}
          {data && (
            <Link to={vp(`/history/${data.id}/edit`)} className="btn btn-primary px-3 py-2 text-sm">
              Edit
            </Link>
          )}
        </div>
      }
    >
      {error && <ErrorNote message={error} />}
      {loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {data && (
        <>
          <div className="card divide-y divide-(--color-border-subtle)">
            {ORDER.map((key) => (
              <MeasurementRow key={key} reading={data} paramKey={key} />
            ))}
          </div>

          <dl className="card mt-4 divide-y divide-(--color-border-subtle) px-4">
            <Detail label="Total chlorine">
              {data.tc === null ? (
                <span className="text-(--color-ink-faint)">
                  Needs both FC and CC
                </span>
              ) : (
                <span className="figure">{Number(data.tc).toFixed(1)} ppm</span>
              )}
            </Detail>
            {/* Only when it was said. Rendering "No" for every reading logged
                before the app could ask would show a fact nobody recorded. */}
            {data.ph_chlorine_neutralised !== null && (
              <Detail label="pH sample">
                {data.ph_chlorine_neutralised
                  ? "Neutralised with R-0007"
                  : "Not neutralised"}
              </Detail>
            )}
            <Detail label="Clarity">
              {data.clarity ? CLARITY_LABELS[data.clarity] : "Not noted"}
            </Detail>
            <Detail label="Test method">
              {data.test_method === "fas_dpd" ? "FAS-DPD drop test" : data.test_method}
            </Detail>
            <Detail label="Entry">{data.entry_mode === "quick" ? "Quick check" : "Full panel"}</Detail>
          </dl>

          <AdvicePanel readingId={data.id} />

          <div className="mt-4">
            <ReportButton
              path={`/api/reports/reading/${data.id}`}
              label="Download as PDF"
            />
          </div>

          {data.notes && (
            <div className="card mt-4 p-4">
              <p className="unit">Notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{data.notes}</p>
            </div>
          )}

          {actionError && (
            <div className="mt-4">
              <ErrorNote message={actionError} />
            </div>
          )}

          <div className="mt-6">
            {confirming ? (
              <div className="card p-4">
                <p className="text-sm font-medium">Delete this reading?</p>
                <p className="mt-1 text-sm text-(--color-ink-muted)">
                  This can't be undone, and any charts covering this date will change.
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={remove}
                    disabled={deleting}
                    className="btn flex-1 bg-(--color-status-high) text-white"
                  >
                    {deleting ? "Deleting…" : "Delete"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="btn btn-quiet flex-1"
                  >
                    Keep it
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-sm text-(--color-ink-muted) underline underline-offset-4"
              >
                Delete this reading
              </button>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}

function MeasurementRow({ reading, paramKey }: { reading: Reading; paramKey: ParamKey }) {
  const meta = PARAMS[paramKey];
  const value = readValue(reading, paramKey);

  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{meta.full}</span>
        <span className="flex items-baseline gap-1.5">
          <span
            className="figure text-2xl"
            style={{ color: value === null ? "var(--color-ink-faint)" : meta.color }}
          >
            {formatValue(value, meta)}
          </span>
          <span className="unit">{meta.unit}</span>
        </span>
      </div>
      <div className="mt-1.5">
        <ComparatorBar
          value={value}
          scaleMin={meta.scaleMin}
          scaleMax={meta.scaleMax}
          color={meta.color}
          label={meta.full}
          size="sm"
        />
      </div>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3">
      <dt className="text-sm text-(--color-ink-muted)">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}
